'use client';

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/shared/utils';

/**
 * Caixa de texto com uma barra de ferramentas embaixo (estilo ClickUp).
 * Padrão do Design System: textarea limpa + barra com chips de ferramentas à
 * esquerda e ações à direita. Traz micro-interações (foco com anel, chips com
 * hover/press) prontas.
 */

/** Classe do chip da barra de ferramentas (use em botões/gatilhos de dropdown). */
export const toolbarChipClass = (ativo = false): string =>
  `inline-flex items-center gap-1.5 rounded-md border border-solid px-2 py-1 text-xs font-medium transition-colors duration-150 ${
    ativo
      ? 'border-brand-200 bg-brand-50 text-brand-700'
      : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50 active:bg-neutral-100'
  }`;

interface ToolbarChipProps {
  icon: LucideIcon;
  ativo?: boolean;
  /**
   * Aparência e semântica de indisponível (`aria-disabled` + esmaecido). O clique
   * continua chegando ao `onClick` de propósito: é ele que explica o motivo ao
   * usuário (toast/aviso) em vez de o chip virar um no-op silencioso.
   */
  indisponivel?: boolean;
  title?: string;
  onClick?: () => void;
  children: ReactNode;
}

/** Chip de ferramenta da barra (ícone + rótulo), com estado ativo opcional. */
export function ToolbarChip({
  icon: Icone,
  ativo = false,
  indisponivel = false,
  title,
  onClick,
  children,
}: ToolbarChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-disabled={indisponivel || undefined}
      className={cn(toolbarChipClass(ativo), indisponivel && 'cursor-not-allowed opacity-60')}
    >
      <Icone className="size-3.5" />
      {children}
    </button>
  );
}

interface ToolbarTextAreaProps {
  value: string;
  onChange: (valor: string) => void;
  placeholder?: string;
  rows?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Conteúdo entre a textarea e a barra (ex.: chips de anexos). */
  abaixoTexto?: ReactNode;
  /** Ferramentas à esquerda da barra (chips, dropdowns). */
  ferramentas?: ReactNode;
  /** Ações à direita da barra (ex.: Cancelar / Publicar). */
  acoes?: ReactNode;
  className?: string;
}

export function ToolbarTextArea({
  value,
  onChange,
  placeholder,
  rows = 4,
  autoFocus = false,
  disabled = false,
  abaixoTexto,
  ferramentas,
  acoes,
  className,
}: ToolbarTextAreaProps) {
  return (
    <div
      className={`focus-within:border-brand-300 focus-within:ring-brand-100 rounded-[12px] border border-solid border-neutral-200 bg-white transition-all duration-150 focus-within:ring-2 ${
        className ?? ''
      }`}
    >
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        autoFocus={autoFocus}
        disabled={disabled}
        className="w-full resize-none border-0 bg-transparent px-3.5 py-3 text-sm text-neutral-800 outline-none placeholder:text-neutral-400 disabled:opacity-50"
      />

      {abaixoTexto ? <div className="px-3.5 pb-2">{abaixoTexto}</div> : null}

      {ferramentas || acoes ? (
        <div className="flex flex-wrap items-center gap-1.5 rounded-b-[12px] border-t border-solid border-neutral-100 bg-neutral-50 px-2.5 py-2">
          {ferramentas}
          {acoes ? <div className="ml-auto flex items-center gap-2">{acoes}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
